import { useState, useCallback } from 'react';
import type { User } from '../types/auth';

const USER_STORAGE_KEY = 'user';

function readUserFromStorage(): User | null {
  const stored = localStorage.getItem(USER_STORAGE_KEY);
  if (!stored) {
    return null;
  }
  try {
    return JSON.parse(stored) as User;
  } catch {
    return null;
  }
}

function writeUserToStorage(user: User): void {
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

function removeUserFromStorage(): void {
  localStorage.removeItem(USER_STORAGE_KEY);
}

export interface UseUserReturn {
  user: User | null;
  setUser: (user: User) => void;
  clearUser: () => void;
}

export const useUser = (): UseUserReturn => {
  const [user, setUserState] = useState<User | null>(() => readUserFromStorage());

  const setUser = useCallback((next: User) => {
    writeUserToStorage(next);
    setUserState(next);
  }, []);

  const clearUser = useCallback(() => {
    removeUserFromStorage();
    setUserState(null);
  }, []);

  return { user, setUser, clearUser };
};
